/**
 * Google Analytics 4 (gtag.js) integration.
 *
 * The measurement ID is read at build time from `VITE_GA_MEASUREMENT_ID`.
 * If unset (typical for local dev), all GA functions become no-ops so we
 * never call out to Google from a developer machine.
 *
 * The gtag.js script itself is normally loaded by the inline snippet that
 * `vite.config.ts` injects into `index.html` at build time — this is what
 * GA4's "Tag installation" verifier scans for. This module only handles
 * SPA page_view tracking on route changes and custom events. If for some
 * reason the inline snippet did not run (e.g. it was stripped from the
 * HTML), this module falls back to injecting gtag.js itself so analytics
 * still works.
 */

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    __uclipseAnalytics?: {
      status: () => Record<string, unknown>;
      test: () => void;
    };
  }
}

const MEASUREMENT_ID = (import.meta.env.VITE_GA_MEASUREMENT_ID ?? '').trim();
let initialized = false;
let missingIdWarned = false;
let lastPath: string | null = null;

export function isAnalyticsEnabled(): boolean {
  return MEASUREMENT_ID.length > 0 && typeof window !== 'undefined';
}

function isLocalhost(): boolean {
  if (typeof window === 'undefined') return true;
  const h = window.location.hostname;
  return h === 'localhost' || h === '127.0.0.1' || h === '::1' || h.endsWith('.local');
}

function warnMissingIdOnce(): void {
  if (missingIdWarned || isLocalhost() || typeof console === 'undefined') return;
  missingIdWarned = true;
  // eslint-disable-next-line no-console
  console.warn(
    '[analytics] VITE_GA_MEASUREMENT_ID was empty at build time, so Google Analytics is disabled. ' +
      'Set the secret in GitHub → Settings → Secrets and variables → Actions, then re-run the workflow.',
  );
}

function installDebugHelper(): void {
  if (typeof window === 'undefined' || window.__uclipseAnalytics) return;
  window.__uclipseAnalytics = {
    status: () => ({
      enabled: isAnalyticsEnabled(),
      initialized,
      measurementId: MEASUREMENT_ID ? maskId(MEASUREMENT_ID) : '(none)',
      gtagReady: typeof window.gtag === 'function',
      gtagScriptInHead: hasStaticGtagScript(),
      dataLayerLength: window.dataLayer?.length ?? 0,
      currentPath: window.location.pathname + window.location.search,
    }),
    test: () => {
      trackEvent('debug_test', { ts: Date.now() });
      // eslint-disable-next-line no-console
      console.info(
        '[analytics] Fired debug_test event. Check GA4 → Reports → Realtime → Event count by Event name within 30s.',
      );
    },
  };
}

/**
 * Returns true when a gtag.js <script> tag is already present in the
 * document head (typically inserted by the inline snippet in index.html).
 * This is the signal we use to know GA4's installation verifier will be
 * able to see the tag, and to avoid double-loading the script at runtime.
 */
function hasStaticGtagScript(): boolean {
  if (typeof document === 'undefined') return false;
  return Boolean(
    document.querySelector('script[src*="googletagmanager.com/gtag/js"]'),
  );
}

/**
 * Bootstrap the gtag dataLayer if the inline snippet did not run. Safe to
 * call multiple times — subsequent calls are no-ops. We disable the
 * automatic initial page_view (`send_page_view: false`) because we send
 * page_views ourselves on route change so SPA navigations and the first
 * paint are tracked the same way.
 */
export function initAnalytics(): void {
  installDebugHelper();
  if (initialized) return;
  if (!isAnalyticsEnabled()) {
    warnMissingIdOnce();
    return;
  }
  initialized = true;

  // Fast path: the inline snippet in index.html already loaded gtag.js
  // and called gtag('config', ID, { send_page_view: false }). Nothing
  // more to do at bootstrap — trackPageView will push events into the
  // existing dataLayer.
  if (typeof window.gtag === 'function' && hasStaticGtagScript()) {
    // eslint-disable-next-line no-console
    console.info(
      `[analytics] Google Analytics ready (${maskId(MEASUREMENT_ID)}, static tag detected). ` +
        `Run window.__uclipseAnalytics.status() / .test() in console to verify.`,
    );
    return;
  }

  // Fallback path: no inline snippet (e.g. index.html was served from a
  // host that did not run our Vite build, or the placeholder was stripped).
  // Inject gtag.js ourselves so analytics still works for real users, even
  // though GA4's installation verifier may not pick it up.
  window.dataLayer = window.dataLayer ?? [];
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer!.push(args);
  };

  window.gtag('js', new Date());
  window.gtag('config', MEASUREMENT_ID, {
    send_page_view: false,
  });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(MEASUREMENT_ID)}`;
  script.addEventListener('error', () => {
    // eslint-disable-next-line no-console
    console.warn(
      '[analytics] Failed to load gtag.js. An ad blocker or network filter is likely blocking www.googletagmanager.com.',
    );
  });
  document.head.appendChild(script);

  // eslint-disable-next-line no-console
  console.info(
    `[analytics] Google Analytics initialised via runtime fallback (${maskId(MEASUREMENT_ID)}). ` +
      `Inline snippet was missing — check that vite.config.ts replaced <!--%GA_SNIPPET%--> in index.html.`,
  );
}

/**
 * Send an SPA page_view to GA4. Includes page_referrer so GA can reconstruct
 * the navigation path correctly across client-side route changes.
 */
export function trackPageView(path: string, title?: string): void {
  if (!isAnalyticsEnabled() || !window.gtag) return;
  const pageLocation = window.location.origin + path;
  const referrer =
    lastPath != null
      ? window.location.origin + lastPath
      : document.referrer || undefined;
  window.gtag('event', 'page_view', {
    page_location: pageLocation,
    page_title: title ?? document.title,
    page_referrer: referrer,
  });
  lastPath = path;
}

/** Fire a custom event (e.g. inquiry_submit). Params are pass-through to GA4. */
export function trackEvent(name: string, params: Record<string, unknown> = {}): void {
  if (!isAnalyticsEnabled() || !window.gtag) return;
  window.gtag('event', name, params);
}

function maskId(id: string): string {
  if (id.length <= 6) return id;
  return `${id.slice(0, 2)}…${id.slice(-4)}`;
}
